import { useState, useEffect, useCallback } from 'react';
import {
    Heading,
    Text,
    Flex,
    Box,
    useDisclosure,
    Button,
    Divider,
    Spacer,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';

import { getBasisById, editBasis, deleteBasis, getBasisDataFilters } from '../../services/basis';
import { checkItemInputEvent } from '../../services/items';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';

import ExpandableItemWrapper from '../layout/ExpandableItemWrapper';
import ShowHideSidebarButton from '../layout/ShowHideSidebarButton';

import InputFormikField from '../forms/InputFormikField';

import PeakPeriodTab from './PeakPeriodTab';
import SpotPriceTab from './SpotPriceTab';
import ForwardPriceTab from './ForwardPriceTab';
import ModelDefinitionsTab from './ModelDefinitionsTab';
import ScalarTab from './ScalarTab';
import DartSpreadTab from './DartSpreadTab';
import OptionalDataTab from './OptionalDataTab';

import Error from '../utils/Error';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import ConfirmationModal from '../utils/ConfirmationModal';
import CircularIconButton from '../utils/CircularIconButton';
import Tooltip from '../utils/Tooltip';
import TabListWrapper from '../utils/TabListWrapper';

import UploadButton from '../itemData/UploadButton';
import DownloadButton from './../itemData/DownloadButton';

import { ReactComponent as TrendIcon } from '../../icons/trend.svg';
import { ReactComponent as DeleteLoadIcon } from '../../icons/delete.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';

const Basis = ({ onBasisEdit, onBasisDelete, options, basisList }) => {
    const { basisId } = useParams();
    const historyPush = useHistoryPush();
    const toast = useCommonToast();
    const intl = useIntl();

    const deleteItemModal = useDisclosure();

    const itemId = Number(basisId);
    const item = 'basis';

    const [selectedBasis, setSelectedBasis] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [inEditDescription, setInEditDescription] = useState(false);
    const [afterUpload, setAfterUpload] = useState(null);

    const [filters, setFilters] = useState(() => getBasisDataFilters(item));

    const [scalarSorting, setScalarSorting] = useState({
        sortColumn: null,
        sortByDescending: true,
    });

    const [dartSpreadSorting, setDartSpreadSorting] = useState({
        sortColumn: null,
        sortByDescending: true,
    });

    const tabs = [
        {
            id: 'peak_periods',
            title: intl.formatMessage({ id: 'basis_tab_peak_periods' }),
        },
        {
            id: 'model_definition',
            title: intl.formatMessage({ id: 'basis_tab_model_definition' }),
        },
        {
            id: 'forward_price',
            title: intl.formatMessage({ id: 'basis_tab_forward_price' }),
        },
        {
            id: 'spot_price',
            title: intl.formatMessage({ id: 'basis_tab_spot_price' }),
        },
        {
            id: 'scaler',
            title: intl.formatMessage({ id: 'basis_tab_scaler' }),
        },
        {
            id: 'sub-hourly_details',
            title: intl.formatMessage({ id: 'basis_tab_sub-hourly_details' }),
            disabled: true,
        },
        {
            id: 'dart_spread',
            title: intl.formatMessage({ id: 'basis_tab_dart_spread' }),
        },
        {
            id: 'optional_data',
            title: intl.formatMessage({ id: 'basis_tab_optional_data' }),
        },
    ];

    const initialFormValues = {
        description: selectedBasis?.description,
    };

    const fetchBasisProperties = useCallback(() => {
        return getBasisById(itemId)
            .then((data) => {
                setSelectedBasis(data);

                // This is needed to show results after previous error
                setLoadingError(false);
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [itemId]);

    useEffect(() => {
        setIsLoading(true);
        fetchBasisProperties();
    }, [fetchBasisProperties]);

    const validateDescription = (value) => {
        const trimmed = value.trim();
        const label = intl.formatMessage({ id: 'basis_name' });

        if (trimmed.length === 0) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label });
        }

        if (trimmed.length > 50) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label, lengthRule: '50 characters or less' }
            );
        }
    };

    const onFormSubmit = async ({ description }) => {
        const tempBasis = {
            ...selectedBasis,
            description,
        };

        updateBasis(tempBasis);

        onBasisEdit({
            id: selectedBasis.basisId,
            description,
        });
    };

    const updateBasis = async (basis) => {
        const successMessage = intl.formatMessage({ id: 'basis_save_changes_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            await editBasis(selectedBasis.basisId, basis);
            setSelectedBasis(basis);

            setInEditDescription(false);

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const onItemDelete = async (targetBasis) => {
        const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'basis' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            await deleteBasis(targetBasis.basisId);

            onBasisDelete(targetBasis);
            historyPush('/basis');

            toast(successMessage);
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const onEditDescription = (isValid) => {
        if (!isValid) {
            return;
        }
        setInEditDescription((prev) => !prev);
    };

    return (
        <ExpandableItemWrapper>
            {isLoading || Object.keys(options).length === 0 ? (
                <DefinitionsSkeleton />
            ) : loadingError ? (
                <Box m={6}>
                    <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="basis" />
                </Box>
            ) : (
                <>
                    <Formik
                        validateOnChange={false}
                        enableReinitialize
                        initialValues={initialFormValues}
                        onSubmit={onFormSubmit}
                    >
                        {({ isSubmitting, dirty, isValid, values, errors, handleSubmit }) => (
                            <Form>
                                <Flex pt={3} pl={6} align="center">
                                    <CustomTrendIcon isMaster={selectedBasis.securityId} />
                                    {!inEditDescription ? (
                                        <Heading px={2} as="h2" variant="h2">
                                            {values.description}
                                        </Heading>
                                    ) : (
                                        <InputFormikField
                                            px={3}
                                            flex="1"
                                            maxW="500px"
                                            validate={(value) =>
                                                validateDescription(value, dirty, isSubmitting, handleSubmit)
                                            }
                                            name="description"
                                            type="text"
                                            onKeyDown={checkItemInputEvent}
                                            isFastField
                                            onBlur={() => {
                                                if (isValid && dirty && !isSubmitting) handleSubmit();
                                            }}
                                        />
                                    )}

                                    <Box display="inline-block" mr={4}>
                                        <Tooltip
                                            label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}
                                        >
                                            <CircularIconButton
                                                aria-label={intl.formatMessage({
                                                    id: 'common_edit_btn_tooltip_and_label',
                                                })}
                                                leftIcon={<EditIcon />}
                                                onClick={() => onEditDescription(isValid)}
                                            />
                                        </Tooltip>
                                    </Box>
                                </Flex>
                            </Form>
                        )}
                    </Formik>

                    <ActionBar>
                        <ShowHideSidebarButton />

                        <Spacer display={{ base: 'none', xl: 'block' }} />

                        <Button
                            leftIcon={<DeleteLoadIcon />}
                            type="button"
                            variant="special"
                            onClick={deleteItemModal.onOpen}
                            size="sm"
                        >
                            <FormattedMessage id="basis_delete_btn" />
                        </Button>
                    </ActionBar>

                    <Divider />

                    <Tabs px={6}>
                        <CustomTabList w="100%" flexWrap={{ base: 'wrap', xl: 'nowrap' }} alignItems="center">
                            <TabListWrapper>
                                {tabs.map((e) => (
                                    <CustomTab key={e.id} isDisabled={e.disabled}>
                                        {e.id === 'sub-hourly_details' ? (
                                            <Tooltip
                                                label={intl.formatMessage({
                                                    id: 'common_feature_in_development',
                                                })}
                                            >
                                                <CustomText> {e.title} </CustomText>
                                            </Tooltip>
                                        ) : (
                                            <CustomText> {e.title} </CustomText>
                                        )}
                                    </CustomTab>
                                ))}
                            </TabListWrapper>

                            <Flex ml="auto" order={{ base: -1, xl: 0 }} w={{ base: '100%', xl: 'auto' }}>
                                <DownloadButton
                                    type="basis"
                                    itemId={itemId}
                                    filters={filters}
                                    hasLabel={true}
                                    itemType="basis"
                                    mr={2}
                                    w="auto !important"
                                    basisDataSorting={{ scalarSorting, dartSpreadSorting }}
                                    bodyMessage={<FormattedMessage id="basis_download_modal_explanation" />}
                                />
                                <UploadButton
                                    type="basis"
                                    itemId={itemId}
                                    hasLabel={true}
                                    mr={4}
                                    w="auto !important"
                                    onAfterUploadChange={() => setAfterUpload((prev) => !prev)}
                                />
                            </Flex>
                        </CustomTabList>

                        <TabPanels mt={2}>
                            <TabPanel>
                                <PeakPeriodTab options={options} basis={selectedBasis} updateBasis={updateBasis} />
                            </TabPanel>
                            <TabPanel>
                                <ModelDefinitionsTab
                                    options={options}
                                    basis={selectedBasis}
                                    updateBasis={updateBasis}
                                />
                            </TabPanel>
                            <TabPanel>
                                <ForwardPriceTab options={options} basis={selectedBasis} updateBasis={updateBasis} />
                            </TabPanel>
                            <TabPanel>
                                <SpotPriceTab options={options} basis={selectedBasis} updateBasis={updateBasis} />
                            </TabPanel>
                            <TabPanel>
                                <ScalarTab
                                    options={options}
                                    basis={selectedBasis}
                                    filters={filters}
                                    onFiltersChange={setFilters}
                                    onSortingChange={setScalarSorting}
                                    item={item}
                                    newDataImported={afterUpload}
                                />
                            </TabPanel>
                            <TabPanel></TabPanel>
                            <TabPanel>
                                <DartSpreadTab
                                    options={options}
                                    basis={selectedBasis}
                                    basisList={basisList}
                                    updateBasis={updateBasis}
                                    onSortingChange={setDartSpreadSorting}
                                    newDataImported={afterUpload}
                                />
                            </TabPanel>
                            <TabPanel>
                                <OptionalDataTab options={options} basis={selectedBasis} updateBasis={updateBasis} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>

                    {deleteItemModal.isOpen && (
                        <ConfirmationModal
                            isOpen
                            onClose={deleteItemModal.onClose}
                            header={<FormattedMessage id="common_delete_modal_heading" values={{ item: 'Basis' }} />}
                            hasExtraStep
                            content={
                                <FormattedMessage
                                    id={
                                        selectedBasis.linkedObjectsCount
                                            ? 'basis_delete_modal_msg'
                                            : 'common_delete_modal_msg'
                                    }
                                    values={{
                                        item: selectedBasis.description,
                                        count: selectedBasis.linkedObjectsCount,
                                    }}
                                />
                            }
                            confirmText={
                                <Box as="span" textTransform="capitalize">
                                    <FormattedMessage id="common_delete" />
                                </Box>
                            }
                            onConfirm={() => {
                                deleteItemModal.onClose();
                                onItemDelete(selectedBasis);
                            }}
                        />
                    )}
                </>
            )}
        </ExpandableItemWrapper>
    );
};

const CustomTrendIcon = styled(TrendIcon, { shouldForwardProp: (prop) => prop !== 'isMaster' })`
    height: 32px;
    width: 32px;
    flex-shrink: 0;

    path {
        fill: ${(props) => (props.isMaster ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-gray-600)')};
    }
`;

const ActionBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
    padding: 12px 24px;
    gap: 16px;

    button {
        text-transform: capitalize;
        width: 100%;
    }

    button:first-of-type {
        margin-top: 4px;
    }

    @media (min-width: 769px) {
        flex-direction: row;

        button:first-of-type {
            margin-top: 0px;
        }
    }

    @media (min-width: 1280px) {
        button {
            width: auto;
        }
    }
`;

const CustomTabList = styled(TabList)`
    overflow-x: auto;
    overflow-y: hidden;
    padding: 5px;
    border-bottom: 1px solid var(--chakra-colors-gray-200);

    [aria-selected='true'] {
        border-bottom: 4px solid var(--chakra-colors-blue-400);
    }
`;

const CustomTab = styled(Tab)`
    padding: 16px 0;
    margin-right: 24px;
    margin-bottom: 0;

    :focus {
        border-radius: var(--chakra-radii-base);
    }

    .tooltip_wrapper {
        transform: translate(-50%, -110%) !important;
    }
`;

const CustomText = styled(Text)`
    white-space: nowrap;
    text-transform: capitalize;
`;

export default Basis;
